import React, { createContext, useContext } from "react";
import { Box, Typography, SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { COLORS } from "../assets/theme";

/**
 * The sidebar icon of the current page. DashboardLayout provides it so page
 * titles match the menu without each page importing its own icon.
 */
export const PageIconContext = createContext<React.ReactNode>(null);

interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Overrides the icon taken from the sidebar; pass `null` to hide it. */
  icon?: React.ReactNode;
  /** Right-aligned slot, e.g. a primary button. */
  actions?: React.ReactNode;
  sx?: SxProps<Theme>;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  icon,
  actions,
  sx,
}) => {
  const contextIcon = useContext(PageIconContext);
  const shownIcon = icon === undefined ? contextIcon : icon;

  return (
    <Box
      sx={[
        {
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: { xs: 1.5, sm: 2 },
          mb: 2.5,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {shownIcon && (
        <Box
          sx={{
            width: { xs: 40, sm: 46 },
            height: { xs: 40, sm: 46 },
            borderRadius: 3,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: COLORS.MainColor,
            background: `linear-gradient(135deg, ${alpha(
              COLORS.MainColor,
              0.16
            )}, ${alpha(COLORS.MainColor, 0.06)})`,
            border: `1px solid ${alpha(COLORS.MainColor, 0.12)}`,
            "& .MuiSvgIcon-root": { fontSize: { xs: "1.3rem", sm: "1.5rem" } },
          }}
        >
          {shownIcon}
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: "1.35rem", sm: "1.65rem" },
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
            color: COLORS.typographyColor,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              mt: 0.4,
              color: "text.secondary",
              fontSize: { xs: "0.82rem", sm: "0.9rem" },
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default PageHeader;
